'use client';

import { ChevronLeftLg, ChevronRightLg } from '@prenuvo/halo-icon';
import { Button } from '@prenuvo/halo-web';
import { useRouter } from 'next/navigation';

import { usePagination } from '@/hooks/usePagination';
import { usePrenuvoStore } from '@/stores/usePrenuvoStore';
import { replaceLocationBasedTerms } from '@/utils/replaceLocationBasedTerms';
import { useShallow } from 'zustand/react/shallow';

interface NavigationButtonsProps {
  onNextPage: () => boolean | Promise<boolean>;
  onPreviousPage: (() => void) | undefined;
}

export default function NavigationButtons({ onNextPage, onPreviousPage }: NavigationButtonsProps) {
  const router = useRouter();
  const { currentIndex, totalSteps, nextStep, previousStep } = usePagination();
  const { locationId } = usePrenuvoStore(
    useShallow((state) => ({
      locationId: state.scan.locationId,
    })),
  );

  const defaultButtonLabel = replaceLocationBasedTerms('Book Now', locationId);

  const nextButtonLabel = currentIndex === totalSteps - 1 ? defaultButtonLabel : 'Next';
  const props = nextButtonLabel === defaultButtonLabel ? {} : { rightIcon: ChevronRightLg };

  const next = async () => {
    const response = await onNextPage();
    if (response) {
      router.push(nextStep?.path() ?? '#');
      // TODO: temporary fix for scrolling to the top as a workaround. We Need to investigate further
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const previous = () => {
    if (onPreviousPage) onPreviousPage();
    router.push(previousStep?.path() ?? '#');
  };

  return (
    <div className="flex items-center gap-3">
      {previousStep && (
        <Button leftIcon={ChevronLeftLg} onClick={previous} size="lg" variant="text">
          Back
        </Button>
      )}

      {nextStep && (
        <Button
          onClick={next}
          size="lg"
          variant="gradient"
          className={{ root: 'w-full md:w-36' }}
          {...props}
        >
          {nextButtonLabel}
        </Button>
      )}
    </div>
  );
}
